import type { Popover } from 'primevue';
import { ref, type Ref } from 'vue';

type PopoverInstance = InstanceType<typeof Popover>;

type SharePopoverController = {
  toggle: (event: MouseEvent) => void;
  hide?: () => void;
  isVisible?: () => boolean;
};

let activeSharePopover: SharePopoverController | null = null;

/**
 * Composable for managing SharePopover component interactions
 * Works with both PrimeVue Popover instances and SharePopover components
 * @returns Object containing the popover ref and toggle function
 */
export function useSharePopover<T = PopoverInstance>() {
  const sharePopoverRef: Ref<T | null> = ref(null);

  const getController = () => {
    return sharePopoverRef.value as SharePopoverController | null;
  };

  const hideShare = () => {
    const instance = getController();
    if (!instance || typeof instance.hide !== 'function') return;

    instance.hide();
  };

  /**
   * Toggles the popover by calling the toggle method
   * Handles both PrimeVue Popover and SharePopover component instances
   * @param event - Mouse event that triggered the toggle (used for popover positioning)
   */
  const toggleShare = (event: MouseEvent) => {
    const instance = getController();
    if (!instance) return;

    const isVisible = instance.isVisible?.() === true;

    if (isVisible) {
      if (typeof instance.hide === 'function') {
        instance.hide();
      } else {
        instance.toggle(event);
      }

      if (activeSharePopover === instance) {
        activeSharePopover = null;
      }

      return;
    }

    if (
      activeSharePopover &&
      activeSharePopover !== instance &&
      typeof activeSharePopover.hide === 'function'
    ) {
      activeSharePopover.hide();
    }

    instance.toggle(event);
    activeSharePopover = instance;
  };

  return {
    sharePopoverRef,
    hideShare,
    toggleShare,
  };
}
